import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, Loader2, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { API_USERS_SIGNIN } from '@/service/url';
import { postApiCall } from '@/service/httpCall';
import { storeUser, Storage } from '@/utilities/setting';
import { ROUTES } from '@/utilities/constant';
import { setUser } from '@/store/setting/actions';

/*
 * LoginPage — logic preserved byte-identical from the shadcn port:
 *   • POST to API_USERS_SIGNIN with { username, password }
 *   • On success: Storage.multiRemove(...), storeUser(resp), dispatch(setUser(resp)),
 *     greeting toast, 2-second delay, navigate to /{ROUTES.DASHBOARD}
 *   • On error: extract from err.response.data.message || err.message || string
 *   • Validation: username required, password required — toast.error prompts
 *   • Field wire-up preserved: name="username", autoComplete="username",
 *     name="password", autoComplete="current-password"
 *
 * Visual chrome: split-screen. Left panel is the theme's primary colour
 * with a concave right edge (radius carved out via border-radius on the
 * right side + negative margin). Right panel holds the sign-in form with
 * subtle rounded inputs (rounded-md, not pill-shaped) so text-boxes look
 * clean and integrated with the rest of the app's neu palette. The whole
 * left panel and the submit button track --primary so switching theme
 * preset (Navy / Indigo / Blue / Emerald / Amber / Slate / custom) shows
 * up here immediately.
 */

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Welcome back';
}

export function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  const validate = (): boolean => {
    if (!username.trim()) {
      toast.error('Username required');
      return false;
    }
    if (!password.trim()) {
      toast.error('Password required');
      return false;
    }
    return true;
  };

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setButtonDisable(true);
    if (!validate()) {
      setButtonDisable(false);
      return;
    }
    const params = { username, password };
    (postApiCall as unknown as (url: string, params: unknown, isAuth?: boolean) => Promise<unknown>)(
      API_USERS_SIGNIN,
      params,
      false,
    )
      .then((resp: unknown) => {
        Storage.multiRemove(['loginTimestamp', 'idleTimestamp']);
        (storeUser as unknown as (data: unknown, cb: () => void) => void)(resp, () => {
          const action = (setUser as unknown as (u: unknown) => { type: string })(resp);
          dispatch(action);
          const greeting = getGreeting();
          const displayName =
            (resp as { username?: string })?.username || username || 'User';
          toast.success(`${greeting} ${displayName}, Welcome back!`);
          setTimeout(() => navigate(`/${ROUTES.DASHBOARD}`), 2000);
        });
      })
      .catch((err: unknown) => {
        console.log('errr', err);
        setButtonDisable(false);
        toast.error(friendlyApiError(err) || 'Login failed');
      });
  };

  return (
    <div className="flex min-h-screen w-screen items-center justify-center bg-background p-4 sm:p-6 lg:p-10">
      <div className="relative flex w-full max-w-5xl overflow-hidden rounded-3xl bg-card shadow-neu-raised">
        {/* ── Left panel — theme primary, concave right edge ─────────── */}
        <div
          className="relative hidden w-1/2 flex-col justify-between p-10 text-white md:flex"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--primary) / 0.9) 0%, hsl(var(--primary)) 100%)',
          }}
        >
          {/* Decorative blobs so the flat brand panel has depth. */}
          <div
            aria-hidden
            className="pointer-events-none absolute -left-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -right-10 h-72 w-72 rounded-full bg-white/10 blur-3xl"
          />

          <div className="relative flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
              <PhoneCall className="h-5 w-5" strokeWidth={2.25} />
            </div>
            <span className="text-lg font-semibold tracking-tight">
              Express IVR
            </span>
          </div>

          <div className="relative space-y-3" style={{ marginBottom: "80px" }}>
            <h2 className="text-4xl font-bold leading-tight tracking-tight text-white/70">
              Hello,
              <br />
              Welcome!
            </h2>
            <p className="max-w-sm text-sm text-white/80">
              Manage inbound and outbound voice at scale — campaigns, IVR flows,
              agent groups, and dispositions in one console.
            </p>
          </div>

          {/* <div className="relative text-[11px] text-white/70">
            © {new Date().getFullYear()} NobleStack. All rights reserved.
          </div> */}

          {/* Concave curve cut out of the right edge of the panel. An
              absolutely-positioned circle in the surrounding card colour
              sits over the right edge — the overlap creates the curve. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 -right-16 hidden w-32 rounded-l-full bg-card md:block"
          />
        </div>

        {/* ── Right panel — sign-in form ─────────────────────────────── */}
        <div className="neu-login flex w-full flex-col justify-center p-8 md:w-1/2 md:p-12">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-8 text-center md:text-left">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                Login
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Sign in to access your dashboard.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {/* username */}
              <div>
                <label
                  htmlFor="username"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  tabIndex={1}
                  className="input-embossed h-11 w-full rounded-md px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
                />
              </div>

              {/* password */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    Password
                  </label>
                  <a
                    href="/auth/recoverpw"
                    className="text-[12px] font-medium text-primary hover:underline"
                    tabIndex={-1}
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    tabIndex={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        submit();
                      }
                    }}
                    className="input-embossed h-11 w-full rounded-md px-4 pr-11 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                    className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* remember me */}
              <label
                htmlFor="remember"
                className="flex cursor-pointer items-center gap-2 text-[13px] text-foreground"
              >
                <input
                  id="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  tabIndex={3}
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                />
                Remember me
              </label>

              {/* submit */}
              <button
                type="submit"
                disabled={buttonDisable}
                tabIndex={4}
                className="mt-2 flex h-11 w-full items-center justify-center rounded-md text-sm font-semibold text-primary-foreground shadow-neu-btn transition-all active:translate-y-px disabled:opacity-70"
                style={{
                  background:
                    'linear-gradient(135deg, hsl(var(--primary) / 0.9) 0%, hsl(var(--primary)) 100%)',
                }}
              >
                {buttonDisable ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Login'
                )}
              </button>
            </form>

            {/* <div className="mt-6 text-center text-[11px] text-muted-foreground md:hidden">
              © {new Date().getFullYear()} NobleStack. All rights reserved.
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}
